import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { useQuery } from "@tanstack/react-query";
import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ShippingLineClient, type ShippingLineDTO } from "../../api/manualInvoice";
import { currencyFromId } from "../../api/part";
import { NexpimClientClient } from "../../api/pricing";

function address(row: ShippingLineDTO): string {
  const line1 = [row.recipient_address_1, row.recipient_address_2].filter(Boolean).join(", ");
  const line2 = [row.recipient_address_city, row.recipient_address_state, row.recipient_address_postal_code]
    .filter(Boolean)
    .join(" ");
  return [line1, line2].filter(Boolean).join(" · ");
}

/**
 * Manual invoices → Shipping: read-only shipping lines carried by imported
 * FedEx/UPS invoices, joined to the SIGM client list in the browser.
 */
export function ShippingInvoiceGrid({ toolbar }: { toolbar: ReactNode }) {
  const { t } = useTranslation();
  const { http } = useAuth();

  const lines = useMemo(() => new ShippingLineClient(http), [http]);
  const clients = useMemo(() => new NexpimClientClient(http), [http]);

  const { data, isLoading } = useQuery({
    queryKey: ["manual-invoice", "shipping"],
    queryFn: () => lines.findAll(),
    staleTime: 60_000,
  });

  const clientsQuery = useQuery({
    queryKey: ["client", "all"],
    queryFn: () => clients.findAll(),
    staleTime: 30 * 60_000,
  });
  const clientMap = useMemo(
    () => new Map((clientsQuery.data ?? []).map((client) => [client.cli_id, client])),
    [clientsQuery.data],
  );

  const cols = useMemo(() => {
    const c = defineColumns<ShippingLineDTO>();
    return [
      c.text("supplier", (row) => row.invoice?.invoice_supplier ?? "", {
        headerKey: "column.invoice-supplier",
        filter: "select",
        priority: 1,
      }),
      c.text("sales_invoice", (row) => row.invoice_no ?? "", { headerKey: "column.sales-invoice", priority: 2 }),
      c.text("sales_invoice_date", (row) => row.invoice_date ?? "", {
        headerKey: "column.sales-invoice-date",
        filter: "daterange",
        defaultVisible: false,
      }),
      c.custom(
        "client",
        (row) => {
          const client = row.invoice_client_id ? clientMap.get(row.invoice_client_id) : undefined;
          return client ? `${client.cli_name1 ?? ""} ${client.cli_no ?? ""}` : "";
        },
        (row) => {
          const client = row.invoice_client_id ? clientMap.get(row.invoice_client_id) : undefined;
          if (!client) return <span />;
          return (
            <span>
              {client.cli_name1}
              <span className="ml-1 text-[11px] text-ink-faint">{client.cli_no}</span>
            </span>
          );
        },
        { headerKey: "column.sales-invoice-client", priority: 3 },
      ),
      c.text("sales_currency", (row) => currencyFromId(row.invoice_currency), {
        headerKey: "column.sales-invoice-currency",
        filter: "select",
        defaultVisible: false,
      }),
      c.custom(
        "shipping_invoice",
        (row) => row.shipping_invoice_no ?? "",
        (row) => (
          <span>
            {row.shipping_invoice_no}
            <span className="ml-1 text-[11px] text-ink-faint">
              {t("manual-invoice.line-no", "Line #")}: {row.shipping_invoice_line_no}
            </span>
          </span>
        ),
        { headerKey: "column.shipping-invoice", priority: 2 },
      ),
      c.text("shipping_invoice_date", (row) => row.shipping_invoice_date ?? "", {
        headerKey: "column.shipping-invoice-date",
        filter: "daterange",
        defaultVisible: false,
      }),
      c.text("shipping_currency", (row) => currencyFromId(row.shipping_invoice_currency), {
        headerKey: "column.shipping-invoice-currency",
        filter: "select",
        defaultVisible: false,
      }),
      c.text("ship_date", (row) => row.ship_date ?? "", {
        headerKey: "column.ship-date",
        filter: "daterange",
        priority: 3,
      }),
      c.text("recipient", (row) => row.recipient_name ?? "", { headerKey: "column.recipient-name", priority: 3 }),
      c.text("address", (row) => address(row), { headerKey: "column.recipient-address", defaultVisible: false }),
      c.text("country", (row) => row.recipient_address_country ?? "", {
        headerKey: "column.recipient-country",
        filter: "select",
        defaultVisible: false,
      }),
      c.number("subtotal", (row) => row.subtotal ?? 0, { headerKey: "column.subtotal", priority: 3 }),
      c.number("tax_1", (row) => row.tax_1 ?? 0, { headerKey: "column.tax-1", defaultVisible: false }),
      c.number("tax_2", (row) => row.tax_2 ?? 0, { headerKey: "column.tax-2", defaultVisible: false }),
    ];
  }, [clientMap, t]);

  return (
    <>
      <DataTable
        data={data ?? []}
        columns={cols}
        getRowId={(row) => String(row.id)}
        toolbarExtra={toolbar}
        loading={isLoading}
        emptyMessage={t("table.empty", "Nothing to show")}
      />
    </>
  );
}
